/**
 * Typed client for the per-account profile + billing reads (ADR-0146):
 *
 *   GET   /v1/account  — any role; returns `{ account, billing }`.
 *   PATCH /v1/account  — owner/admin; allow-list `{ name }`. Per-account
 *                        rate limit 30/min on the server.
 *
 * Discriminated-union results mirror the pattern in
 * `account-buyer-detail-retention.ts` so the calling view branches on
 * `result.kind` rather than catching exceptions.
 *
 * Wire body uses snake_case per CLAUDE.md §3. The `BillingWire`
 * surfaced here is always the read-only Discovery default when the
 * account has no staff-assigned `account_billing` row (ADR-0142 P1);
 * an assigned plan surfaces as `plan_label`, but the panel never
 * exposes a self-serve upgrade — `managed_by: 'sill'` pins that.
 */

import type {
  AccountResponse,
  AccountUpdateResponse,
  AccountBillingWire,
  AccountWire,
} from '@sill/types/api';
import { AuthRateLimitError } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

export type GetAccountResult =
  | {
      readonly kind: 'ok';
      readonly account: AccountWire;
      readonly billing: AccountBillingWire;
    }
  | { readonly kind: 'error'; readonly message: string };

export type PatchAccountResult =
  | { readonly kind: 'ok'; readonly account: AccountWire }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'bad_request'; readonly reason?: string | undefined }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

/* --------------------------------------------------------------------- */

export async function getAccount(
  http: HttpClient,
): Promise<GetAccountResult> {
  try {
    const res = await http.request<AccountResponse>({
      method: 'GET',
      path: '/v1/account',
    });
    if (res === undefined || res.account === undefined || res.billing === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    return { kind: 'ok', account: res.account, billing: res.billing };
  } catch (err) {
    if (err instanceof ApiError) {
      return { kind: 'error', message: explainApiError(err) };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

export async function patchAccount(
  http: HttpClient,
  // ADR-0160 — the server PATCH is a partial update: send `name` and/or
  // `display_time_zone` (a valid IANA zone, or `null` to clear → UTC default).
  body: {
    readonly name?: string;
    readonly display_time_zone?: string | null;
  },
): Promise<PatchAccountResult> {
  try {
    const res = await http.request<AccountUpdateResponse>({
      method: 'PATCH',
      path: '/v1/account',
      body,
    });
    if (res === undefined || res.account === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    return { kind: 'ok', account: res.account };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'bad_request') {
        return { kind: 'bad_request', reason: err.server_reason };
      }
      return { kind: 'error', message: explainApiError(err) };
    }
    if (err instanceof AuthRateLimitError) {
      return {
        kind: 'rate_limited',
        message: "You've changed this too many times. Wait a minute and try again.",
      };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

function explainApiError(err: ApiError): string {
  switch (err.kind) {
    case 'service_unavailable':
      return 'Account service is temporarily unavailable.';
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return 'Network error — check your connection.';
    case 'bad_response':
      return 'Server returned an unexpected response.';
    default:
      return err.message;
  }
}
